import React from 'react';
import { Box, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { glass, ADMIN_GRADIENTS } from './glassTokens';

/**
 * GlassCard — the primary card primitive for the admin glassmorphism system.
 *
 * Props:
 *   intensity    — 'default' | 'strong'   (default: 'default')
 *   interactive  — boolean  adds hover translateY + shadow
 *   gradientRing — boolean  adds a thin gradient border ring on top edge
 *   sx           — MUI sx override
 *   children
 */
export default function GlassCard({
  intensity = 'default',
  interactive = false,
  gradientRing = false,
  sx = {},
  children,
  ...rest
}) {
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const g = glass(theme);
  const surf = intensity === 'strong' ? g.surfaceStrong : g.surface;

  const hoverProps = interactive && !reducedMotion
    ? { whileHover: { y: -2, boxShadow: '0 16px 40px rgba(2,6,23,0.55)' } }
    : {};

  return (
    <Box
      component={motion.div}
      transition={{ duration: 0.18 }}
      {...hoverProps}
      sx={{
        ...surf,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        ...(gradientRing && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: ADMIN_GRADIENTS.accent,
            zIndex: 1,
          },
        }),
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.mode === 'dark' ? '#6366F1' : '#4F46E5'}`,
          outlineOffset: 2,
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}
